import { useEffect, useRef, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Home, RefreshCw } from 'lucide-react'
import { useThemeScope } from '@hooks/useThemeScope'
import { Button } from './Button'
import { ErrorIllustration } from './ErrorIllustration'

export type ErrorCode = 403 | 404 | 500 | 502 | 503 | 504 | 'offline'

const SERVER_CODES: ErrorCode[] = [500, 502, 503, 504, 'offline']
// 502, 503 and 504 usually clear on their own, so those pages retry for the user.
const AUTO_RETRY: ErrorCode[] = [502, 503, 504]
const AUTO_RETRY_SECONDS = 30

interface ErrorPageProps {
  code: ErrorCode
  /** Called by "Try again". Defaults to a full reload. */
  onRetry?: () => void
  /** Extra primary action, e.g. "Sign in again" on a 403. */
  action?: { label: string; onClick: () => void }
  /** Technical detail shown under the actions. Callers pass it only in development builds. */
  details?: string
  /** Fill the screen. Defaults to true for server errors, false for 403 and 404 (they sit inside the layout). */
  fullScreen?: boolean
}

/** Section root for "go home": stay inside the portal the user is in. */
function homePath(pathname: string) {
  if (pathname.startsWith('/tenant')) return '/tenant'
  if (pathname.startsWith('/super-admin')) return '/super-admin'
  return '/'
}

function stepsKey(code: ErrorCode) {
  if (code === 'offline') return 'offline'
  return code === 500 ? 'server' : 'gateway'
}

export function ErrorPage({ code, onRetry, action, details, fullScreen }: ErrorPageProps) {
  useThemeScope() // built from tokens, so it supports dark mode even outside the portal layouts
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const isServer = SERVER_CODES.includes(code)
  const full = fullScreen ?? isServer
  const retry = onRetry ?? (() => window.location.reload())
  const [now] = useState(() => new Date())
  const [seconds, setSeconds] = useState(AUTO_RETRY_SECONDS)
  const auto = AUTO_RETRY.includes(code)

  // Keep the latest retry without restarting the timers whenever the parent re-renders.
  const retryRef = useRef(retry)
  retryRef.current = retry

  useEffect(() => {
    if (!auto) return
    const id = window.setInterval(() => setSeconds((s) => Math.max(0, s - 1)), 1000)
    return () => window.clearInterval(id)
  }, [auto])

  useEffect(() => {
    if (auto && seconds === 0) {
      retryRef.current()
      setSeconds(AUTO_RETRY_SECONDS)
    }
  }, [auto, seconds])

  // Back online: reconnect without making the user press anything.
  useEffect(() => {
    if (code !== 'offline') return
    const onOnline = () => retryRef.current()
    window.addEventListener('online', onOnline)
    return () => window.removeEventListener('online', onOnline)
  }, [code])

  const steps = isServer ? (t(`errorPage.steps.${stepsKey(code)}`, { returnObjects: true }) as string[]) : []
  const codeLabel = code === 'offline' ? '···' : String(code)

  return (
    <div
      role="alert"
      className={full ? 'flex min-h-screen items-center bg-canvas px-4 py-10' : 'flex min-h-[60vh] items-center px-4 py-12'}
    >
      <div
        className={`mx-auto grid w-full items-center gap-10 ${isServer ? 'max-w-5xl lg:grid-cols-2' : 'max-w-md text-center'}`}
      >
        <div className={isServer ? 'order-2 lg:order-1' : ''}>
          {isServer ? (
            <p className="select-none text-7xl font-extrabold leading-none tracking-tight text-primary-soft [-webkit-text-stroke:2px_rgb(var(--primary))] sm:text-8xl">
              {codeLabel}
            </p>
          ) : (
            <p className="text-sm font-semibold tracking-wider text-primary">{code}</p>
          )}
          <h1 className="mt-3 text-3xl font-bold text-fg">{t(`errorPage.${code}.title`)}</h1>
          <p className={`mt-3 text-base leading-relaxed text-muted ${isServer ? 'max-w-md' : 'mx-auto max-w-sm'}`}>
            {t(`errorPage.${code}.text`)}
          </p>

          {isServer && (
            <div className="mt-6 rounded-xl border bg-surface p-4">
              <p className="text-sm font-semibold text-fg">{t('errorPage.whatToDo')}</p>
              <ul className="mt-2 space-y-2 text-sm text-muted">
                {steps.map((step, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-primary-soft text-xs font-semibold text-primary">
                      {i + 1}
                    </span>
                    <span>{step}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className={`mt-6 flex flex-wrap items-center gap-3 ${isServer ? '' : 'justify-center'}`}>
            {isServer && (
              <Button onClick={retry} leftIcon={<RefreshCw className="h-4 w-4" />}>
                {auto ? t('errorPage.retryNow') : t('errorPage.tryAgain')}
              </Button>
            )}
            {action && <Button onClick={action.onClick}>{action.label}</Button>}
            <Button variant="outline" onClick={() => navigate(homePath(pathname))} leftIcon={<Home className="h-4 w-4" />}>
              {t('errorPage.goHome')}
            </Button>
            {!isServer && !action && (
              <Button variant="ghost" onClick={() => navigate(-1)} leftIcon={<ArrowLeft className="h-4 w-4" />}>
                {t('errorPage.goBack')}
              </Button>
            )}
          </div>

          {auto && (
            <p className="mt-4 flex items-center gap-2 text-sm text-muted" aria-live="polite">
              <RefreshCw className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
              {t('errorPage.retryingIn', { seconds })}
            </p>
          )}

          {isServer && (
            <p className="mt-6 text-xs text-faint">
              {t('errorPage.reference', { code: code === 'offline' ? 'offline' : code, time: now.toLocaleTimeString() })}
              <br />
              {t('errorPage.contactAdmin')}
            </p>
          )}

          {details && (
            <pre className="mt-6 max-h-40 overflow-auto rounded-lg bg-surface-sunken p-3 text-left text-xs text-muted">{details}</pre>
          )}
        </div>

        {isServer && (
          <div className="order-1 flex justify-center lg:order-2">
            <ErrorIllustration code={code} />
          </div>
        )}
      </div>
    </div>
  )
}
